import type { ReactNode } from 'react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface BoardSectionHeaderProps {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  /** Id of the section body this header expands and folds. */
  controlsId: string;
  /** Text beside the label; clicking it toggles like the chevron. */
  summary?: ReactNode;
  /** Right-aligned controls. */
  children?: ReactNode;
}

/** One 28px line: chevron toggle, label, optional summary, trailing controls. */
export function BoardSectionHeader({
  label,
  collapsed,
  onToggle,
  controlsId,
  summary,
  children,
}: BoardSectionHeaderProps) {
  const Chevron = collapsed ? CaretRightIcon : CaretDownIcon;
  return (
    <div className="flex h-7 shrink-0 items-center gap-2 border-b border-border bg-background px-2 text-xs">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={controlsId}
        className="flex shrink-0 items-center gap-1 font-medium"
      >
        <Chevron aria-hidden className="h-3 w-3" />
        {label}
      </button>
      {summary != null && (
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          className={cn(
            'min-w-0 flex-1 truncate text-left text-muted-foreground',
            !collapsed && 'invisible',
          )}
        >
          {summary}
        </button>
      )}
      {children}
    </div>
  );
}
